












































// Composition
import { ref, defineComponent, Ref } from '@nuxtjs/composition-api'

// Store
import { useSearchStore } from '@/store/search'

// Composables
import useFacets from '@/composables/search/useFacets'
import { useCountry } from '~/composables/country/useCountry'

// Components
import FacetSelected from '@/components/feature/search/facets/FacetSelected.vue'
import FacetBlockLoopInner from '@/components/feature/search/facets/FacetBlockLoopInner.vue'

// Types
import { FacetInterface, FacetObjectInterface } from '@/types/search/searchTypes'

// Common
import { SEARCH_TYPES } from '@/common/static/search'
import { multiSelectFacets, FACETNAMES } from '@/common/static/facets'

export default defineComponent({
  name: 'FacetBlockLoop',

  components: {
    FacetSelected,
    FacetBlockLoopInner,
  },

  props: {
    options: {
      type: Object,
      required: true,
    },
  },

  setup(props) {
    // create v-model ref for input
    const { isHierarchical, mapToFacetName, facetKeySelected } = useFacets()
    const searchStore = useSearchStore()
    const { facets } = searchStore
    const useCache = facets.cachedFacets[props.options.key as keyof typeof facets.cachedFacets] !== undefined
    const facetBlockLoopRef = ref(null) as Ref<HTMLElement | null>
    const facetObject: Array<FacetObjectInterface> = useCache
      ? facets.cachedFacets[props.options.key as keyof typeof facets.cachedFacets]
      : [{}]
    const cachedItems = useCache ? (facetObject[0].data as Array<FacetInterface>) : []
    const searchForFacet = ref('')
    const { isCN } = useCountry()

    const getFilteredItems = () => {
      if (props.options.facet[0].data === undefined) {
        // This is a nested facet
        return props.options.facet
      }

      // store items
      const items = props.options.facet[0].data

      return orderAndFilterItems(items)
    }

    const getEmptyItems = () => {
      if (props.options.facet[0].data === undefined) {
        return []
      }

      const items = props.options.facet[0].data

      // work with cached items and show items that difference with current items
      // only do this for multi select facets
      let filteredCachedFacets = [] as Array<FacetInterface>
      if (multiSelectFacets.includes(props.options.key)) {
        const itemsValueArray = items.map((item: FacetInterface) => item.value)
        filteredCachedFacets = cachedItems
          .filter((cachedFacet: FacetInterface) => !itemsValueArray.includes(cachedFacet.value))
          .map((item: FacetInterface) => {
            return {
              count: 0,
              value: item.value,
            }
          })
      }

      return orderAndFilterItems(filteredCachedFacets)
    }

    const orderAndFilterItems = (mergedItems: Array<FacetInterface>) => {
      // filter based on search input field data
      const filteredItems = mergedItems.filter((item: FacetInterface) => {
        return item.value.toLowerCase().includes(searchForFacet.value.toLowerCase())
      })

      // do not sort alphabically for product category
      if (props.options.key === SEARCH_TYPES.CATEGORIES) {
        return filteredItems
      }

      // for all other facets, return sorted values
      return filteredItems.sort((a: FacetInterface, b: FacetInterface) => {
        return a.value.localeCompare(b.value)
      })
    }

    const addIndent = () => {
      return props.options.indent + 3
    }

    const facetClicked = () => {
      // reset filter
      searchForFacet.value = ''

      // scroll this div to the top
      if (facetBlockLoopRef.value !== null) {
        facetBlockLoopRef.value.scrollTop = 0
      }
    }

    return {
      isCN,
      searchForFacet,
      getFilteredItems,
      getEmptyItems,
      addIndent,
      isHierarchical,
      SEARCH_TYPES,
      multiSelectFacets,
      mapToFacetName,
      facetClicked,
      facetBlockLoopRef,
      facetKeySelected,
      FACETNAMES,
    }
  },
})
